import React from "react";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { Eyebrow } from "@/components/ui-kit";
import { CTASection } from "@/components/CTASection";
import { IMG, CREDENTIALS } from "@/data/content";
import { ABOUT, EXPERIENCE } from "@/data/site";
import { SEO } from "@/data/seo";

const PRINCIPLES = [
  { n: "01", t: "Practitioners, not overhead", b: "We embed as focused specialists delivering specific outcomes — not consultants layered above your team producing decks." },
  { n: "02", t: "Outcomes, not activity", b: "Status reports don't ship programs. We measure success by what gets delivered, not by how busy everyone looks." },
  { n: "03", t: "Priced against reality", b: "Engagements are structured against fully-loaded FTE cost, so the value is legible on day one — not buried in a rate card." },
  { n: "04", t: "Built to flex — and to exit", b: "We scale with your portfolio and structure every engagement to leave you stronger, with capability that survives our departure." },
];

export default function About() {
  useSeo({ ...SEO["/about"], image: IMG.founder, path: "/about" });

  return (
    <>
      <PageHero
        eyebrow="About PMN"
        titleLines={["Built from real-world", "delivery — not", "consulting theory."]}
        subtitle="PMN exists because complex programs rarely fail for lack of plans. They fail when execution discipline isn't owned by someone senior and accountable."
        image={IMG.glassEye}
      />

      {/* Founder */}
      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
            <Reveal className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full border border-teal/30" />
                <img
                  src={IMG.founder}
                  alt="Brandon Rodgers, Founder & Managing Member of Project Management Network"
                  loading="lazy"
                  decoding="async"
                  width="896"
                  height="1088"
                  className="relative w-full object-cover"
                  data-testid="founder-portrait"
                />
                <div className="relative mt-5 flex items-center justify-between border-t border-charcoal/10 pt-5">
                  <div>
                    <p className="font-serif text-lg tracking-wideish text-charcoal">Brandon Rodgers</p>
                    <p className="text-sm text-teal" data-testid="founder-title">{ABOUT.founderTitle}</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="lg:col-span-7">
              <Reveal>
                <Eyebrow>The firm</Eyebrow>
                <h2 className="mt-6 text-3xl font-normal leading-[1.15] tracking-tightish text-charcoal sm:text-4xl">
                  {ABOUT.firmTitle}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 space-y-6 text-base leading-[1.8] text-charcoal/70" data-testid="about-firm-copy">
                  {ABOUT.paragraphs.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
                </div>
              </Reveal>

              {/* Credentials */}
              <Reveal delay={0.15}>
                <div className="mt-12">
                  <p className="text-xs uppercase tracking-mega text-teal-deep">Founder credentials</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    {CREDENTIALS.map((c) => (
                      <span key={c} className="border border-charcoal/15 px-4 py-2 text-sm tracking-wideish text-charcoal/80">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Track record */}
      <section className="relative overflow-hidden bg-charcoal py-24 text-paper lg:py-32">
        <div className="relative mx-auto max-w-8xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow dark>Properly attributed experience</Eyebrow>
            <h2 className="mt-6 max-w-2xl text-3xl font-normal leading-[1.15] tracking-tightish sm:text-4xl lg:text-5xl">
              The delivery record behind the firm.
            </h2>
            <p className="mt-6 max-w-2xl text-sm leading-[1.8] text-paper/60" data-testid="about-attribution">{EXPERIENCE.attribution}</p>
          </Reveal>

          <div className="mt-16 grid gap-px overflow-hidden border border-paper/15 bg-paper/15 sm:grid-cols-2 lg:grid-cols-4">
            {EXPERIENCE.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="bg-charcoal">
                <div className="h-full p-9 lg:p-10">
                  <p className="font-serif text-5xl tracking-tightish text-paper lg:text-6xl">{s.value}</p>
                  <p className="mt-4 text-sm leading-[1.7] text-paper/60">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-12 max-w-3xl text-base leading-[1.8] text-paper/70">{EXPERIENCE.note}</p>
          </Reveal>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How we operate</Eyebrow>
            <h2 className="mt-6 max-w-2xl text-3xl font-normal leading-[1.15] tracking-tightish text-charcoal sm:text-4xl lg:text-5xl">
              The convictions behind every engagement.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden border border-charcoal/10 bg-charcoal/10 md:grid-cols-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="bg-paper">
                <div className="h-full p-10 lg:p-12">
                  <span className="font-serif text-3xl text-teal">{p.n}</span>
                  <h3 className="mt-5 text-xl font-normal tracking-tightish text-charcoal">{p.t}</h3>
                  <p className="mt-4 text-sm leading-[1.8] text-charcoal/65">{p.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Work with PMN"
        title="Want to talk about your situation?"
        body="Every engagement starts with a conversation. Tell us what's stuck, at risk, or launching — and we'll tell you straight whether we can help."
        image={IMG.boardroomWarm}
      />
    </>
  );
}
